import React, { useEffect, useState } from "react";
import { api, stageClass, STAGES, fmtDate, priorityClass } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Plus, Upload, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import NewApplicationDialog from "../components/NewApplicationDialog";
import BulkImportApplicationsDialog from "./BulkImportApplicationsDialog";
import BulkActionsBar from "../components/BulkActionsBar";
import NeedsAttention from "../components/NeedsAttention";
import { CATEGORIES, usePerms } from "../lib/perms";

export default function Applications() {
  const [apps, setApps] = useState(null);
  const [meta, setMeta] = useState({ partners: [], colleges: [], intakes: [], users: [] });
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [attn, setAttn] = useState(false);
  const [qText, setQText] = useState("");
  useEffect(() => { const t = setTimeout(() => setFilters(f => (f.q || "") === qText ? f : { ...f, q: qText }), 300); return () => clearTimeout(t); }, [qText]);
  const perms = usePerms();
  const nav = useNavigate();

  const load = async () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([_, v]) => v && v !== "all"));
    const [a, m] = await Promise.all([api.get(`/applications?${qs}`), api.get("/meta")]);
    setApps(a.data); setMeta(m.data);
  };
  useEffect(() => { load(); setSelected([]); /* eslint-disable-next-line */ }, [filters]);

  const F = ({ k, ph, items, w = "w-[200px]", tid }) => (
    <Select value={filters[k] || "all"} onValueChange={v => setFilters({ ...filters, [k]: v })}>
      <SelectTrigger data-testid={tid} className={`h-9 ${w} bg-white`}><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent><SelectItem value="all">All {ph}</SelectItem>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  const rows = apps || [];
  const allSel = rows.length > 0 && selected.length === rows.length;
  const toggle = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Applications</h1>
          <p data-testid="apps-count" className="text-sm text-slate-500 mt-1">{apps === null ? "Loading…" : `${rows.length} application${rows.length !== 1 ? "s" : ""}`}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="apps-needs-attention-btn" variant="outline" onClick={() => setAttn(true)}><AlertCircle className="w-4 h-4 mr-2 text-amber-600" /> Needs Attention</Button>
          {perms.import_export && <Button data-testid="apps-import-btn" variant="outline" onClick={() => setImportOpen(true)}><Upload className="w-4 h-4 mr-2" /> Bulk Import</Button>}
          {perms.add && <Button data-testid="apps-add-btn" onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-2" /> Add Application</Button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-xl border border-slate-200">
        <input placeholder="Search student, ID, course…" value={qText} onChange={e => setQText(e.target.value)}
          data-testid="apps-search-input" className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white w-64" />
        <F k="stage" ph="Stages" tid="apps-stage-filter" items={STAGES.map(s => [s, s])} />
        <F k="program_category" ph="Categories" tid="apps-category-filter" items={[...CATEGORIES.map(c => [c, c]), ["__none__", "Needs review (no category)"]]} />
        <F k="partner_id" ph="Partners" tid="apps-partner-filter" items={meta.partners.map(p => [p.id, p.company_name])} />
        <F k="college" ph="Colleges" w="w-[220px]" tid="apps-college-filter" items={(meta.colleges || []).map(c => [c.id, c.code ? `${c.code} — ${c.name}` : c.name])} />
      </div>

      {(perms.edit || perms.reassign) && <BulkActionsBar selected={selected} filters={filters} total={rows.length} meta={meta} perms={perms} onDone={load} />}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3 w-8"><input type="checkbox" data-testid="apps-select-all" checked={allSel} onChange={() => setSelected(allSel ? [] : rows.map(a => a.id))} /></th>
                {["App ID", "Student", "Partner", "College / Course", "Category", "Intake", "Stage", "Priority", "Date"].map(h => <th key={h} className="text-left px-4 py-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(a => {
                const partner = meta.partners.find(p => p.id === a.partner_id);
                return (
                  <tr key={a.id} data-testid={`apps-row-${a.application_id}`} onClick={() => nav(`/applications/${a.id}`)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}><input type="checkbox" data-testid={`apps-select-${a.application_id}`} checked={selected.includes(a.id)} onChange={() => toggle(a.id)} /></td>
                    <td className="px-4 py-3 mono text-xs text-slate-700">{a.application_id}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{a.student_name}<div className="text-xs text-slate-500">{a.email}</div></td>
                    <td className="px-4 py-3 text-slate-700">{partner?.company_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <span title={a.college} className="mono text-xs font-semibold text-slate-800">{a.college_code || a.college || "—"}</span>
                      <div className="text-xs text-slate-500 truncate max-w-[220px]" title={a.course_name || a.program}>{a.course_name || a.program || "No course name"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{a.program_category || <span className="text-amber-700">Needs review</span>}</td>
                    <td className="px-4 py-3 text-slate-700">{a.intake}</td>
                    <td className="px-4 py-3"><span className={`stage-badge ${stageClass(a.stage)}`}>{a.stage}</span></td>
                    <td className="px-4 py-3"><span className={`stage-badge ${priorityClass(a.priority)}`}>{a.priority}</span></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(a.date_received)}</td>
                  </tr>
                );
              })}
              {apps === null && <tr><td colSpan={10} data-testid="apps-loading" className="text-center py-12 text-slate-500">Loading applications…</td></tr>}
              {apps !== null && rows.length === 0 && <tr><td colSpan={10} className="text-center py-12 text-slate-500">No applications match your filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <NewApplicationDialog open={open} onOpenChange={setOpen} meta={meta} onCreated={(a) => { setOpen(false); nav(`/applications/${a.id}`); }} />
      <BulkImportApplicationsDialog open={importOpen} onOpenChange={setImportOpen} meta={meta} onImported={load} />
      <NeedsAttention open={attn} onOpenChange={setAttn} />
    </div>
  );
}
